import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
// import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
import { useColorModeValue } from 'toolkit/chakra/color-mode';
import { Image } from 'toolkit/chakra/image';
// import useIssueUrl from 'lib/hooks/useIssueUrl';
import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { copy } from 'toolkit/utils/htmlEntities';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
// import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import FooterSocialItem from './FooterSocialitem';
// import IntTxsIndexingStatus from './IntTxsIndexingStatus';
// import { block } from 'configs/app/ui/views';
// import getApiVersionUrl from './utils/getApiVersionUrl';

const MAX_LINKS_COLUMNS = 4;

// const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${ config.UI.footer.frontendVersion }`;
// const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${ config.UI.footer.frontendCommit }`;

const Footer = () => {

  const BLOCKSCOUT_SOCIAL = React.useMemo(() => [
    // {
    //   icon: 'social/new/linkedin' as const,
    //   iconSize: '18px',
    //   text: 'Linkedin',
    //   url: 'https://www.linkedin.com/company/emoneynetwork',
    // },
    {
      icon: 'social/new/telegram' as const,
      iconSize: '24px',
      text: 'Telegram',
      url: 'https://t.me/Emoney_io',
    },
    {
      icon: 'social/new/gitdoc' as const,
      iconSize: '18px',
      text: 'Gitdocs',
      url: 'https://blog.emoney.io/',
    },
    {
      icon: 'social/new/twitter' as const,
      iconSize: '18px',
      text: 'Twitter',
      url: 'https://x.com/emoney_network',
    },
    // {
    //   icon: 'social/new/instagram' as const,
    //   iconSize: '18px',
    //   text: 'Instagram',
    //   url: 'https://www.instagram.com/emoneynetwork/',
    // },
  ], []);
  const PRODUCTS_LINKS = [

    {
      icon: 'social/git' as const,
      iconSize: '18px',
      text: 'Whitelist',
      url: 'https://whitelist.emoney.network/login',
    },
    {
      icon: 'social/twitter' as const,
      iconSize: '18px',
      text: 'Faucet',
      url: 'https://faucet.emoney.network/faucet',
    },
    {
      icon: 'social/discord' as const,
      iconSize: '18px',
      text: 'Bridge',
      url: 'https://dashboard.emoney.io/bridge',
    },
    {
      icon: 'brands/blockscout' as const,
      iconSize: '18px',
      text: 'Download Wallet',
      url: 'https://chromewebstore.google.com/detail/e-money-network/ndklagefmdogpfkffimjdnbnplllndic?hl=en&authuser=0',
    },
  ];
  const RESOURCES_LINKS = [

    {
      icon: 'social/git' as const,
      iconSize: '18px',
      text: 'Whitepaper',
      url: 'https://whitepaper.emoney.network/',
    },
    {
      icon: 'social/twitter' as const,
      iconSize: '18px',
      text: 'Technical',
      url: 'https://docs.emoney.network/',
    },
    {
      icon: 'social/discord' as const,
      iconSize: '18px',
      text: 'RPC Details',
      url: 'https://docs.emoney.network/add-e-money-network-to-metamask',
    },
  ];

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: [ 'footer-links' ],
    queryFn: async() => fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;
  const logoSrc = useColorModeValue(config.UI.navigation.logo.default, config.UI.navigation.logo.dark || config.UI.navigation.logo.default);
  const INVERT_FILTER = 'brightness(0) invert(1)';
  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    const logoColor = { base: 'blue.600', _dark: 'white' };

    return (
      <Box gridArea={ gridArea }>
        <Flex columnGap={ 2 } textStyle="xs" alignItems="center">
          <Link href="https://emoney.io/" target="_blank" display="inline-flex" color={ logoColor } _hover={{ color: logoColor }}>
            <Image
              width="100px"
              height="40px"
              src={ logoSrc }
              alt={ `${ config.chain.name } network logo` }
              display="block"
              filter={{ _dark: !config.UI.navigation.logo.dark ? undefined : INVERT_FILTER }}
              objectFit="contain"
              objectPosition="left"
            />
          </Link>
        </Flex>
        <Text mt={ 3 } fontSize="xs">
          Gateway to the Next-Gen Cross-Chain Payment Solutions
        </Text>
        <Grid
          gridArea={{ lg: 'links-bottom' }}
          gap={ 1 }
          gridTemplateColumns={{
            base: 'repeat(auto-fill, 30px)',
            lg: 'repeat(1, 30px)',
            xl: 'repeat(1, 30px)',
          }}
          gridTemplateRows={{
            base: 'auto',
            lg: 'repeat(1, auto)',
            xl: 'repeat(1, auto)',
          }}
          gridAutoFlow={{ base: 'row', lg: 'column' }}
          alignContent="start"
          justifyContent={{ lg: 'start' }}
          mt={{ base: 8, lg: 4 }}
          opacity={{ base: 0.6, _dark: 1 }}
          _dark={{ filter: 'invert(0.7)', opacity: 1 }}
        >
          { BLOCKSCOUT_SOCIAL.map(link => <FooterSocialItem { ...link } key={ link.text }/>) }
        </Grid>
        <Box mt={ 6 } alignItems="start" textStyle="xs">
          <Text>
            { copy }  All Rights Reserved 2024-{ (new Date()).getFullYear() }
          </Text>
        </Box>
      </Box>
    );
  }, [ BLOCKSCOUT_SOCIAL, logoSrc ]);

  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'border.divider',
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${ CONTENT_MAX_WIDTH }px`,
    m: '0 auto',
  };

  const renderRecaptcha = (gridArea?: GridProps['gridArea']) => {
    if (!config.services.reCaptchaV2.siteKey) {
      return <Box gridArea={ gridArea }/>;
    }

    return (
      <Box gridArea={ gridArea } textStyle="xs" mt={ 6 }>
        <Link href="https://scallop-user-img-bucket.s3.eu-west-1.amazonaws.com/Privacy+Policy+V2-2024.pdf" external noIcon>Privacy Policy</Link>
        <span> and </span>
        <Link href="https://scallop-user-img-bucket.s3.eu-west-1.amazonaws.com/Terms+and+Conditions+V2-2024.pdf" external noIcon>Terms & Conditions</Link>
        <span> apply.</span>
      </Box>
    );
  };

  if (config.UI.footer.links) {
    return (
      <Box { ...containerProps }>
        <Grid { ...contentProps }>
          <div>
            { renderProjectInfo() }
            { renderRecaptcha() }
          </div>

          <Grid
            gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
            gridTemplateColumns={{
              base: 'repeat(auto-fill, 160px)',
              lg: `repeat(${ colNum }, 135px)`,
              xl: `repeat(${ colNum }, 160px)`,
            }}
            justifyContent={{ lg: 'flex-end' }}
            mt={{ base: 8, lg: 0 }}
          >
            {
              ([
                { title: 'Blockscout', links: PRODUCTS_LINKS },
                ...(linksData || []),
              ])
                .slice(0, colNum)
                .map(linkGroup => (
                  <Box key={ linkGroup.title }>
                    <Skeleton fontWeight={ 500 } mb={ 3 } display="inline-block" loading={ isPlaceholderData }>{ linkGroup.title }</Skeleton>
                    <VStack gap={ 1 } alignItems="start">
                      { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.text } isLoading={ isPlaceholderData }/>) }
                    </VStack>
                  </Box>
                ))
            }
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box { ...containerProps }>
      <Grid
        { ...contentProps }
        gridTemplateAreas={{
          lg: `
          "network links-top"
          "info links-bottom"
          "recaptcha links-bottom"
        `,
        }}
      >

        { renderProjectInfo({ lg: 'info' }) }
        { renderRecaptcha({ lg: 'recaptcha' }) }

        <Grid
          gridArea={{ lg: 'links-bottom' }}
          gap={ 6 }
          gridTemplateColumns={{
            base: '1fr',
            lg: 'repeat(2, 200px)',
          }}
          alignContent="start"
          justifyContent={{ lg: 'flex-end' }}
          mt={{ base: 8, lg: 0 }}
        >
          <Box>
            <Text fontWeight={ 500 } mb={ 3 }>Products</Text>
            <VStack gap={ 1 } alignItems="start">
              { PRODUCTS_LINKS.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
            </VStack>
          </Box>
          <Box>
            <Text fontWeight={ 500 } mb={ 3 }>Resources</Text>
            <VStack gap={ 1 } alignItems="start">
              { RESOURCES_LINKS.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
            </VStack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
